import { useCallback, useEffect, useState } from 'react';
import { Line } from '@picsart/web-layering/types';

import { IORHistory, IORHistoryApply, MaskType } from 'types';
import { ToolTypes } from 'configs/constants';
import useWindowListener from 'hooks/common/useWindowListener';
import canvasToBlobUrl from 'utils/formatters/canvasToBlobUrl';
import loadingImage from 'utils/images/loadingImage';
import { isRedoEvent, isUndoEvent } from 'utils/common/hotKeyCombinations';
import errorLogger from 'services/errorLogger';
import { UndoRedoTypes } from 'components/common/UndoRedo';

interface IEraserHistory {
  lineUrl: string;
  resultUrl: string;
  toolType?: ToolTypes;
  appliedRemoverTool: string | null;
  lines?: Line[];
  isReset?: boolean;
  appliedMaskImg?: string;
}
type HistoryStepType = 1 | -1;
export type AddHistoryType = (mask: IORHistory, appliedRemoverTool?: string | null) => void;
type UseToolUndoRedoReturnType = [IEraserHistory[], number, boolean, AddHistoryType,
  (step: HistoryStepType, type: UndoRedoTypes) => void, UndoRedoTypes | null, (type: UndoRedoTypes | null) => void];

const useToolUndoRedo = (callback: (mask: IORHistoryApply, newStep: number, appliedRemoverTool?: string | null, type?: UndoRedoTypes | null) => void,
  defaultMask: MaskType | undefined,
  condition: boolean): UseToolUndoRedoReturnType => {
  const [history, setHistory] = useState<IEraserHistory[]>([]);
  const [historyType, setHistoryType] = useState<UndoRedoTypes | null>(null);
  const [historyStep, setHistoryStep] = useState<number>(-1);
  const [historyDisabled, setHistoryDisabled] = useState<boolean>(false);
  const [cutHistory, setCutHistory] = useState<boolean>(false);

  useEffect(() => {
    if (!cutHistory) {
      return;
    }

    setHistory(prev => prev.slice(0, historyStep + 1));
  }, [historyStep, cutHistory]);

  const undoRedoHandler = useCallback(async (step: HistoryStepType, undoRedoTypes: UndoRedoTypes) => {
    const newStep = historyStep + step;
    setHistoryType(undoRedoTypes);

    if (newStep >= history.length || newStep < -1) {
      return;
    }

    setHistoryDisabled(true);

    try {
      let maskImg = defaultMask;
      let linesArray: Line[] | undefined;
      let appliedRemoverTool = null;

      if (newStep !== -1) {
        if (history[newStep].lineUrl) {
          maskImg = await loadingImage(history[newStep].lineUrl);
        }

        if (history[newStep].lines) {
          linesArray = history[newStep].lines;
        }

        appliedRemoverTool = history[newStep].appliedRemoverTool;
      }

      setCutHistory(false);

      callback({
        appliedMaskImg: history[newStep]?.appliedMaskImg,
        lineImage: maskImg,
        isReset: history[newStep]?.isReset,
        line: linesArray,
        resultUrl: history[newStep]?.resultUrl,
      }, newStep, appliedRemoverTool, historyType);
      setHistoryStep(newStep);
    } catch (e) {
      errorLogger.trigger(e, 'error');
    } finally {
      setHistoryDisabled(false);
    }
  }, [historyStep, history, defaultMask, callback, historyType]);

  const undoRedoWithHotKeyHandler = useCallback((event?: KeyboardEvent) => {
    if (!condition || !event) {
      return;
    }
    const step = isUndoEvent(event) ? -1 : isRedoEvent(event) ? 1 : 0;
    step && undoRedoHandler(step, isUndoEvent(event) ? UndoRedoTypes.UNDO : UndoRedoTypes.REDO);
  }, [condition, undoRedoHandler]);

  useWindowListener('keydown', undoRedoWithHotKeyHandler);

  const addEraserHistory = useCallback(async (newMask, appliedRemoverTool) => {
    const newStep = historyStep + 1;
    setHistoryDisabled(true);
    setCutHistory(true);
    setHistoryStep(newStep);
    const maskUrl = newMask.lineImage instanceof HTMLCanvasElement ? await canvasToBlobUrl(newMask.lineImage) : newMask.lineImage?.src;

    setHistory(prev => {
      const newHistory = prev.slice();
      newHistory.splice(newStep, 0, {
        lineUrl: maskUrl || '',
        isReset: newMask.isReset,
        lines: newMask.line,
        appliedMaskImg: newMask.appliedMaskImg,
        resultUrl: newMask.resultUrl,
        toolType: newMask?.toolType,
        appliedRemoverTool,
      });
      return newHistory;
    });

    setHistoryDisabled(false);
  }, [historyStep]);

  return [
    history,
    historyStep,
    historyDisabled,
    addEraserHistory,
    undoRedoHandler,
    historyType,
    setHistoryType,
  ];
};

export default useToolUndoRedo;
